// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreview.module.css';
import type {TextualAttachmentLanguagePopoutProps} from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreviewUtils';
import {useHighlightLanguageOptions} from '@app/features/code_highlighting/utils/ArboriumHighlighting';
import {
	SearchableListPopout,
	type SearchableListPopoutItem,
	type SearchableListPopoutSection,
} from '@app/features/ui/popover/searchable_list_popout/SearchableListPopout';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

const SEARCH_LANGUAGE_CODE_DESCRIPTOR = msg({
	message: 'Search language code...',
	comment: 'Button or menu action label in the channel and chat textual attachment language popout. Keep it concise.',
});
const SEARCH_LANGUAGE_CODE_2_DESCRIPTOR = msg({
	message: 'Search language code',
	comment: 'Button or menu action label in the channel and chat textual attachment language popout. Keep it concise.',
});
const CODE_LANGUAGE_OPTIONS_DESCRIPTOR = msg({
	message: 'Code language options',
	comment: 'Short label in the channel and chat textual attachment language popout. Keep it concise.',
});
const NO_RESULTS_FOUND_DESCRIPTOR = msg({
	message: 'No results found',
	comment: 'Empty-state text in the channel and chat textual attachment language popout.',
});
export const TextualAttachmentLanguagePopout = observer(function TextualAttachmentLanguagePopout({
	defaultSearchQuery,
	onClose,
	onSelectLanguage,
	selectedLanguage,
}: TextualAttachmentLanguagePopoutProps) {
	const {i18n} = useLingui();
	const highlightLanguageOptions = useHighlightLanguageOptions();
	const handleLanguageSelect = useCallback(
		(languageCode: string) => {
			onSelectLanguage(languageCode);
			onClose();
		},
		[onClose, onSelectLanguage],
	);
	const sections = useMemo<Array<SearchableListPopoutSection>>(() => {
		const languageItems: Array<SearchableListPopoutItem> = highlightLanguageOptions.map((languageOption) => ({
			id: languageOption.code,
			ariaLabel: languageOption.code,
			searchValues: [languageOption.code, languageOption.canonicalCode],
			isSelected: languageOption.code === selectedLanguage,
			onSelect: () => {
				handleLanguageSelect(languageOption.code);
			},
			render: () => (
				<>
					<span
						className={styles.languageOptionCode}
						data-flx="channel.embeds.attachments.textual-attachment-language-popout.language-items.language-option-code"
					>
						{languageOption.code}
					</span>
					{languageOption.canonicalCode !== languageOption.code && (
						<span
							className={styles.languageOptionCanonical}
							data-flx="channel.embeds.attachments.textual-attachment-language-popout.language-items.language-option-canonical"
						>
							{languageOption.canonicalCode}
						</span>
					)}
				</>
			),
		}));
		return [
			{
				id: 'languages',
				items: languageItems,
			},
		];
	}, [handleLanguageSelect, highlightLanguageOptions, selectedLanguage]);
	return (
		<SearchableListPopout
			className={styles.languagePopout}
			scrollerClassName={styles.languageScroller}
			placeholder={i18n._(SEARCH_LANGUAGE_CODE_DESCRIPTOR)}
			searchInputAriaLabel={i18n._(SEARCH_LANGUAGE_CODE_2_DESCRIPTOR)}
			listAriaLabel={i18n._(CODE_LANGUAGE_OPTIONS_DESCRIPTOR)}
			defaultSearchQuery={defaultSearchQuery}
			noResultsLabel={i18n._(NO_RESULTS_FOUND_DESCRIPTOR)}
			sections={sections}
			onRequestClose={onClose}
			data-flx="channel.embeds.attachments.textual-attachment-language-popout.language-popout"
		/>
	);
});
